/**
 * RedactedRecipientsTable.jsx — table for BYO/transactional campaigns.
 *
 * NEVER shows a full phone number. Only:
 *   - client_ref (customer's own ID)
 *   - to_hash (first 8 chars of the SHA hash)
 *   - masked phone (e.g. "••••3210") only if available within 72h post-send window
 *
 * Props:
 *   data: [{ client_ref, to_hash, phone_mask, status, sent_at, delivered_at, read_at, failure_reason }]
 *   loading: boolean
 *   onRowClick?: (row) => void
 */

import React from 'react';
import { Table, Tag, Tooltip } from 'antd';
import { LockFilled } from '@ant-design/icons';

const STATUS_COLOR = {
  queued: 'default',
  sent: 'blue',
  delivered: 'green',
  read: 'cyan',
  failed: 'red',
};

const fmt = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch (_) { return String(v); }
};

export default function RedactedRecipientsTable({ data = [], loading = false, onRowClick }) {
  const columns = [
    {
      title: (
        <span>
          client_ref{' '}
          <Tooltip title="The ID your server sent us. Use this to correlate on your side.">
            <span style={{ color: '#8c8c8c' }}>ⓘ</span>
          </Tooltip>
        </span>
      ),
      dataIndex: 'client_ref',
      key: 'client_ref',
      render: (v) => v ? <code>{v}</code> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'to_hash',
      dataIndex: 'to_hash',
      key: 'to_hash',
      render: (v) => v ? (
        <Tooltip title={v}>
          <code style={{ color: '#666' }}>{String(v).slice(0, 8)}…</code>
        </Tooltip>
      ) : '—',
    },
    {
      title: (
        <span>
          phone <LockFilled style={{ color: '#bfbfbf', fontSize: 11 }} />
        </span>
      ),
      dataIndex: 'phone_mask',
      key: 'phone_mask',
      render: (v) => v ? (
        <span style={{ fontFamily: 'monospace' }}>{v}</span>
      ) : (
        <Tooltip title="Phone number purged after 72h or never stored">
          <span style={{ color: '#bfbfbf' }}>redacted</span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{v || 'unknown'}</Tag>,
    },
    { title: 'Sent', dataIndex: 'sent_at', key: 'sent_at', render: fmt },
    { title: 'Delivered', dataIndex: 'delivered_at', key: 'delivered_at', render: fmt },
    { title: 'Read', dataIndex: 'read_at', key: 'read_at', render: fmt },
    {
      title: 'Failure',
      dataIndex: 'failure_reason',
      key: 'failure_reason',
      render: (v) => v ? <span style={{ color: '#cf1322', fontSize: 12 }}>{v}</span> : '—',
    },
  ];

  return (
    <Table
      size="small"
      rowKey={(r) => r.id || r.message_id || `${r.to_hash}-${r.client_ref}`}
      columns={columns}
      dataSource={data}
      loading={loading}
      onRow={(record) => ({
        onClick: () => onRowClick?.(record),
        style: onRowClick ? { cursor: 'pointer' } : undefined,
      })}
      pagination={{ pageSize: 25, showSizeChanger: true }}
      scroll={{ x: 'max-content' }}
    />
  );
}
